import React, { useState, useEffect } from 'react';
import { IoSpeedometer, IoLocationSharp } from 'react-icons/io5';
import { RiPaintBrushFill } from 'react-icons/ri';
import { FaBed, FaBath } from 'react-icons/fa';
import { SlSizeFullscreen } from 'react-icons/sl';
import { FaSchool } from 'react-icons/fa6';
import axios from 'axios';
import { formatNumberComma, formatNumber } from '../utils/numberUtils';

const HouseList = ({ data = [], type = '', condition = true }) => {
  const defaultCarImage = '/car-placeholder.jpg';
  const [images, setImages] = useState({});
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchStreetViewImage = async (address, id) => {
      try {
        const url = `https://maps.googleapis.com/maps/api/streetview`;
        const response = await axios.get(url, {
          params: {
            size: '600x300',
            location: address,
            key: process.env.REACT_APP_GOOGLE_API,
          },
          responseType: 'blob',
        });

        const imageBlob = URL.createObjectURL(response.data);
        setImages((prevImages) => ({ ...prevImages, [id]: imageBlob }));
      } catch (error) {
        console.error('Error fetching the Street View image:', error);
        setErrors((prevErrors) => ({
          ...prevErrors,
          [id]: 'Failed to fetch the Street View image.',
        }));
        setImages((prevImages) => ({ ...prevImages, [id]: defaultCarImage }));
      }
    };

    data.forEach((car) => {
      if (
        !car?.featured_image_url &&
        !car?.imageUrls?.[0] &&
        car?.address?.address
      ) {
        fetchStreetViewImage(car.address.address, car.id);
      }
    });
  }, [data]);

  return (
    <div className='card-list'>
      {data.map((car, index) => {
        const address = car?.formattedAddress || car?.address?.address || '';
        const fallbackStreetView = `https://maps.googleapis.com/maps/api/streetview?size=600x300&location=${encodeURIComponent(
          address
        )}&key=${process.env.REACT_APP_GOOGLE_API}`;

        const imageSrc =
          car?.featured_image_url ||
          car?.imageUrls?.[0] ||
          images[car.id] ||
          fallbackStreetView ||
          defaultCarImage;

        return (
          <div key={index} className='list-item'>
            <a
              href={`/details/?detail_id=${car.id}${
                car.id.length > 5 && type !== '' ? `&type=${type}` : ''
              }`}
            >
              <div>
                <img
                  src={imageSrc}
                  alt='Car'
                  className='car-image'
                  onError={(e) => {
                    if (e.target.src !== defaultCarImage) {
                      e.target.src = defaultCarImage;
                    }
                  }}
                  loading='lazy'
                />
              </div>
            </a>

            <div className='list-content'>
              <h3>
                <a
                  href={`/details/?detail_id=${car.id}${
                    car.id.length > 5 && type !== '' ? `&type=${type}` : ''
                  }`}
                  dangerouslySetInnerHTML={{
                    __html: car?.title?.rendered || car?.formattedAddress,
                  }}
                ></a>
              </h3>

              {car?.dealer?.name && (
                <span className='car-attr'>
                  <strong>Dealer:</strong>{' '}
                  <a href={car?.vdp_url}>{car?.dealer?.name}</a>
                </span>
              )}
              {car?.meta?.hp_location && (
                <div className='car-attr'>
                  <span>
                    <IoLocationSharp size={18} />
                    {car?.meta?.hp_location}
                  </span>
                </div>
              )}
              {(car?.meta?.hp_h_fs_beds ||
                car?.meta?.hp_h_fs_baths ||
                car?.meta?.hp_size_sq_ft ||
                car?.meta?.hp_school_district ||
                car?.bedrooms ||
                car?.bathrooms ||
                car?.squareFootage) && (
                <div className='car-attr-wrap'>
                  {Number(car?.meta?.hp_h_fs_beds) > 0 && (
                    <div className='car-attr'>
                      <span>
                        <FaBed size={18} />
                        {car?.meta?.hp_h_fs_beds} beds
                      </span>
                    </div>
                  )}
                  {Number(car?.bedrooms) > 0 && (
                    <div className='car-attr'>
                      <span>
                        <FaBed size={18} />
                        {car?.bedrooms} beds
                      </span>
                    </div>
                  )}
                  {Number(car?.meta?.hp_h_fs_baths) > 0 && (
                    <div className='car-attr'>
                      <span>
                        <FaBath size={16} />
                        {car?.meta?.hp_h_fs_baths} baths
                      </span>
                    </div>
                  )}
                  {Number(car?.bathrooms) > 0 && (
                    <div className='car-attr'>
                      <span>
                        <FaBath size={16} />
                        {car?.bathrooms} baths
                      </span>
                    </div>
                  )}
                  {car?.meta?.hp_size_sq_ft > 0 && (
                    <div className='car-attr'>
                      <span>
                        <SlSizeFullscreen size={12} />
                        {formatNumberComma(car.meta.hp_size_sq_ft)} sq.ft
                      </span>
                    </div>
                  )}
                  {car?.squareFootage > 0 && (
                    <div className='car-attr'>
                      <span>
                        <SlSizeFullscreen size={12} />
                        {formatNumberComma(car.squareFootage)} sq.ft
                      </span>
                    </div>
                  )}
                  {car?.meta?.hp_school_district && (
                    <div className='car-attr'>
                      <span>
                        <FaSchool size={18} />
                        {car?.meta?.hp_school_district}
                      </span>
                    </div>
                  )}
                </div>
              )}

              <div className='car-attr'>
                {car?.meta?.hp_v_details_mileage ? (
                  <span>
                    <IoSpeedometer />{' '}
                    {formatNumberComma(car.meta.hp_v_details_mileage)} miles
                  </span>
                ) : car?.miles ? (
                  <span>
                    <IoSpeedometer /> {formatNumberComma(car.miles)} miles
                  </span>
                ) : null}
                {car?.hp_listing_v_details_exterior_co?.[0]?.name ? (
                  <span>
                    <RiPaintBrushFill />{' '}
                    {car?.hp_listing_v_details_exterior_co?.[0]?.name}
                  </span>
                ) : car?.exterior_color ? (
                  <span>
                    <RiPaintBrushFill /> {car?.exterior_color}
                  </span>
                ) : null}
              </div>

              {car?.meta?.hp_price ? (
                <span className='car-attr price-tag'>
                  ${formatNumber(car?.meta?.hp_price)}
                </span>
              ) : car?.price ? (
                <span className='car-attr price-tag'>
                  ${formatNumber(car?.price)}
                </span>
              ) : null}
            </div>
          </div>
        );
      })}
      {Object.values(errors).length > 0 && <p>Some images failed to load.</p>}
    </div>
  );
};

export default HouseList;
